import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Avatar, Icon, useInteractiveRow } from '../../../ui/index.js';
import { NotificationDot } from '../../notifications/index.js';
import { formatRequestDensity } from '../density.js';
import { formatRequestDestination } from '../destination-display.js';
import { formatRelative, formatSLA, formatVolume, formatWeight } from '../format.js';
import { formatRequestOrigin } from '../origin-display.js';
import type { RequestView } from '../types.js';
import { CarrierResponsesBar } from './CarrierResponsesBar.js';
import { RequestListThumbnail, RequestListThumbnailPlaceholder } from './RequestListThumbnail.js';
import { RequestStatusPill } from './RequestStatusPill.js';
import { UnreadBadge } from './UnreadBadge.js';
import { RequestSelectionCheckbox } from '../selection/RequestSelectionCheckbox.js';

export function RequestListRow({
  r,
  now,
  selected,
  selectionMode = false,
  selectionChecked = false,
  onSelect,
  onToggleSelection = () => undefined,
  unread,
  notificationCount,
}: {
  r: RequestView;
  now: number;
  selected: boolean;
  selectionMode?: boolean;
  selectionChecked?: boolean;
  onSelect: () => void;
  onToggleSelection?: (shiftKey: boolean) => void;
  unread: { unreadCount: number; isMarkedUnread: boolean };
  // INVARIANT (#1072): zero-or-omitted renders nothing; >0 shows the
  //   amber notification badge alongside (not replacing) the chat
  //   unread red badge.
  notificationCount?: number;
}) {
  const { t } = useTranslation('requests');
  const { background, handlers } = useInteractiveRow({ selected, onActivate: onSelect });
  const sla = formatSLA(
    {
      status: r.status,
      slaDeadline: r.slaDeadline,
      inWorkAt: r.inWorkAt,
      closedAt: r.closedAt,
      processedAt: r.processedAt,
    },
    now,
  );
  const archived = r.status === RequestStatus.ARCHIVE;
  const op = archived ? 0.75 : 1;
  const thumbnailItem = r.items.find((item) => item.photoKeys.length > 0);
  const density = formatRequestDensity(r.weight, r.volume);
  const hasMessageAttention =
    unread.unreadCount > 0 || unread.isMarkedUnread || (notificationCount ?? 0) > 0;

  const cellStyle = (firstCol: boolean, extra: React.CSSProperties = {}): React.CSSProperties => ({
    padding: '0 10px',
    height: 'var(--s-10)',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-2)',
    background,
    cursor: 'pointer',
    transition: 'background var(--dur-1) var(--ease-out)',
    boxShadow: selected && firstCol ? 'inset 3px 0 0 var(--accent-1)' : 'none',
    ...extra,
  });

  return (
    <>
      {selectionMode && (
        <div style={cellStyle(true, { justifyContent: 'center', cursor: 'default' })}>
          <RequestSelectionCheckbox
            checked={selectionChecked}
            label={t('export.bulk.selectRequest', { id: r.id })}
            onToggle={onToggleSelection}
          />
        </div>
      )}
      <div style={cellStyle(!selectionMode, { opacity: op })} {...handlers}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)' }}>
          {r.id}
        </span>
      </div>
      <div style={cellStyle(false, { opacity: op, minWidth: 0, gap: 8 })} {...handlers}>
        {thumbnailItem?.photoKeys[0] ? (
          <RequestListThumbnail
            requestId={r.id}
            itemId={thumbnailItem.id}
            objectKey={thumbnailItem.photoKeys[0]}
          />
        ) : (
          <RequestListThumbnailPlaceholder />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--s-1)',
            minWidth: 0,
            flex: '1 1 0',
          }}
        >
          <div
            data-testid="request-name-line"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              color: 'var(--fg-1)',
              fontWeight: 500,
              minWidth: 0,
            }}
          >
            <span
              data-testid="request-name-text"
              title={r.name}
              style={{
                minWidth: 0,
                flex: '1 1 0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {r.name}
            </span>
            {r.kind === 'CONTAINER' && (
              <span
                data-testid="request-container-badge"
                style={{
                  flexShrink: 0,
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: 'var(--r-1)',
                  background: 'var(--bg-3)',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--border-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('list.row.containerBadge')}
              </span>
            )}
            {r.kind === 'SIMPLE' && (
              <span
                data-testid="request-simple-badge"
                style={{
                  flexShrink: 0,
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: 'var(--r-1)',
                  background: 'var(--bg-3)',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--border-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('list.row.simpleBadge')}
              </span>
            )}
          </div>
          <div
            className="small"
            data-testid="request-route-line"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              minWidth: 0,
            }}
          >
            <Icon name="arrow-right" size={11} />
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {formatRequestOrigin(r, t('draftDefaults.from'))} →{' '}
              {r.kind === 'CONTAINER'
                ? t('list.row.containerDestination')
                : formatRequestDestination(r, { short: true })}
            </span>
          </div>
        </div>
      </div>
      <div
        data-testid="request-messages-cell"
        style={cellStyle(false, {
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 'var(--s-1)',
        })}
        {...handlers}
        role={hasMessageAttention ? handlers.role : undefined}
        tabIndex={hasMessageAttention ? handlers.tabIndex : undefined}
      >
        <UnreadBadge unreadCount={unread.unreadCount} isMarkedUnread={unread.isMarkedUnread} />
        {notificationCount !== undefined && notificationCount > 0 ? (
          <NotificationDot count={notificationCount} />
        ) : null}
      </div>
      <div style={cellStyle(false)} {...handlers}>
        <RequestStatusPill status={r.status} />
      </div>
      <div style={cellStyle(false)} {...handlers}>
        <CarrierResponsesBar carriers={r.carriers} />
      </div>
      <div style={cellStyle(false, { justifyContent: 'flex-end' })} {...handlers}>
        <div
          data-testid="request-list-metrics"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            fontSize: 'var(--fz-11)',
            lineHeight: 1.35,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          <div data-testid="request-list-weight">
            {r.weight == null ? (
              <>
                <span style={{ color: 'var(--fg-4)' }}>—</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.weightSuffix')}</span>
              </>
            ) : (
              <>
                <span style={{ color: 'var(--fg-1)' }}>{formatWeight(r.weight)}</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.weightSuffix')}</span>
              </>
            )}
          </div>
          <div data-testid="request-list-volume">
            {r.volume == null ? (
              <>
                <span style={{ color: 'var(--fg-4)' }}>—</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.volumeSuffix')}</span>
              </>
            ) : (
              <>
                <span style={{ color: 'var(--fg-1)' }}>{formatVolume(r.volume)}</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.volumeSuffix')}</span>
              </>
            )}
          </div>
          <div data-testid="request-list-density">
            {density === '—' ? (
              <>
                <span style={{ color: 'var(--fg-4)' }}>—</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.densitySuffix')}</span>
              </>
            ) : (
              <>
                <span style={{ color: 'var(--fg-1)' }}>{density}</span>{' '}
                <span style={{ color: 'var(--fg-3)' }}>{t('list.row.densitySuffix')}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        style={cellStyle(false, { opacity: op, minWidth: 0 })}
        data-sla-level={sla.level}
        {...handlers}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {sla.level === 'none' ? (
            <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>—</span>
          ) : (
            <span
              data-testid="sla-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: sla.level === 'ok' ? 400 : 500,
                // WHY: explicit nowrap on the primary SLA line so the
                // longest Russian formatter outputs never wrap on
                // desktop. Mobile keeps two-line layout via the
                // parent column.
                whiteSpace: 'nowrap',
                color:
                  sla.level === 'err'
                    ? 'var(--status-error-fg)'
                    : sla.level === 'warn'
                      ? 'var(--status-warn-fg)'
                      : sla.level === 'success'
                        ? 'var(--status-success-fg)'
                        : 'var(--fg-2)',
              }}
            >
              <Icon name={sla.level === 'err' ? 'timer-off' : 'timer'} size={12} />
              {sla.text}
            </span>
          )}
          <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
            {t('list.row.createdPrefix')} {formatRelative(r.createdAt, now)}
          </div>
          <div data-testid="request-row-updated-at" style={{ fontSize: 11, color: 'var(--fg-4)' }}>
            {t('list.row.updatedPrefix')} {formatRelative(r.updatedAt, now)}
          </div>
        </div>
      </div>
      <div style={cellStyle(false, { opacity: op, minWidth: 0 })} {...handlers}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--fg-2)',
            fontSize: 12,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Avatar name={r.manager.name} size={20} />
          <span
            data-testid="request-list-manager-name"
            title={r.manager.name}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              flex: '1 1 0',
            }}
          >
            {r.manager.name}
          </span>
        </div>
      </div>
    </>
  );
}
